"use client";

import React from "react";
import { motion } from "framer-motion";
import { Check, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface PricingOption {
  title: string;
  duration: string;
  price: string;
  includes: string[];
}
const PRICING_OPTIONS: PricingOption[] = [{
  title: "Biomechanics-Led Gait & Balance Assessment",
  duration: "30 minutes",
  price: "£120",
  includes: ["Musculoskeletal history", "Pressure-mat gait assessment", "Balance assessment", "Interpretation", "Physiotherapy advice report"]
}, {
  title: "Physiotherapy-Led Gait & Balance Assessment",
  duration: "60 minutes",
  price: "£150",
  includes: ["Clinical physiotherapy assessment", "Pressure-mat gait assessment", "Balance assessment", "Interpretation", "Treatment where appropriate", "Physiotherapy advice and management plan"]
}];
const BOOKING_URL = "https://physiogenics.uk3.cliniko.com/bookings?business_id=1933385715966552600";
export default function GaitPricing() {
  return <section id="gaitandbalancepricing" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4
      }} className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground mb-4">
            Pressure-mat gait and balance assessment
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Our pressure-mat system provides objective information about foot loading, gait and balance that can complement clinical and biomechanical assessment.
          </p>
        </motion.div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 max-w-4xl mx-auto mb-12">
          {PRICING_OPTIONS.map((option, index) => <motion.div key={option.title} data-index={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: index * 0.1
        }} whileHover={{
          y: -4
        }} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border shadow-xs hover:shadow-md transition-shadow duration-200 bg-card">
                <div>
                  <CardHeader className="space-y-3 pb-4">
                    <div className="flex items-center justify-between gap-2">
                      <Badge variant="secondary" className="gap-1 font-normal py-1 px-2.5">
                        <Clock className="size-3.5 text-muted-foreground" />
                        <span>{option.duration}</span>
                      </Badge>
                    </div>
                    <CardTitle className="text-xl sm:text-2xl font-semibold leading-snug">
                      {option.title}
                    </CardTitle>
                    <div className="pt-2">
                      <span className="text-4xl font-bold tracking-tight text-foreground">
                        {option.price}
                      </span>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-2 pb-6">
                    <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">
                      Includes:
                    </div>
                    <ul className="space-y-2.5">
                      {option.includes.map((item, itemIndex) => <li key={itemIndex} data-index={itemIndex} className="flex items-start gap-2.5 text-sm sm:text-base text-foreground/90">
                          <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                            <Check className="size-3" />
                          </span>
                          <span>{item}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                </div>

                <CardFooter className="pt-0">
                  <Button asChild size="lg" className="w-full">
                    <Link to={BOOKING_URL} newTab>
                      Book this assessment
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Global Bottom Navigation Button */}
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.25
      }} className="text-center">
          <Button asChild variant="outline" size="lg">
            <Link to="/GaitBalanceAssessmentPage">
              Explore Gait &amp; Balance
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}